import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { ToastProvider } from '@/components/ui/toast';
import { App } from '@/App';
import { AuthProvider } from '@/lib/auth';
import { I18nProvider } from '@/lib/i18n';
import { IslandProvider } from '@/lib/islands';
import { ODSTEP_SPOKOJNY } from '@/lib/odswiezanie';
import { ThemeProvider } from '@/lib/theme';
import '@/index.css';

/**
 * ODŚWIEŻANIE: EKRAN MA DOGONIĆ RZECZYWISTOŚĆ SAM.
 *
 * ⚠️ POPRZEDNIE USTAWIENIE BYŁO ŹRÓDŁEM ZGŁOSZENIA (2026-08-18): `refetchOnWindowFocus: false`
 * i `staleTime: 30_000` bez żadnego odpytywania znaczyły, że raz wczytany ekran nie pobierze już
 * NIC. A stan zmiany konfiguracji zmienia się na maszynie klienta — nie w odpowiedzi na kliknięcie
 * w portalu — więc zegarek „zmiana w toku" wisiał do ręcznego odświeżenia. Tak samo nowy automat
 * czy pracownik nie miał jak się pojawić na otwartej liście.
 *
 * ⚠️ `refetchOnWindowFocus` JEST TU NAJWAŻNIEJSZY, nie odpytywanie. Tak się z tym pracuje: portal
 * stoi w drugiej karcie obok RDP do maszyny klienta, a powrót do niego jest dokładnie tą chwilą,
 * w której człowiek chce zobaczyć skutek. Jedno żądanie na powrót, zamiast setki w tle.
 *
 * ⚠️ `staleTime` OBNIŻONY DO 10 s: to on decyduje, czy przejście między ekranami pobiera dane
 * na nowo. Przy 30 s wracało z pamięci i wyglądało jak zawieszony stan.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 10_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      /**
       * ⚠️ ODSTĘP SPOKOJNY JAKO DOMYŚLNY — szybki mają tylko ekrany z czymś w toku (patrz
       * `lib/odswiezanie.ts`). W TLE NIE ODPYTUJEMY: `refetchIntervalInBackground` zostaje
       * `false`, więc schowana karta nie generuje ruchu, którego nikt nie widzi.
       */
      refetchInterval: ODSTEP_SPOKOJNY,
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Brak elementu #root');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <I18nProvider>
          <ToastProvider>
            <AuthProvider>
              <IslandProvider>
                <BrowserRouter>
                  <App />
                </BrowserRouter>
              </IslandProvider>
            </AuthProvider>
          </ToastProvider>
        </I18nProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
